<template>
  <div class="activities-costs-section">
    <div class="activities-costs-section__title">
      {{ activitiesCostsSectionTitle }}
    </div>
    <div class="activities-costs-section__description">
      <custom-block-content :blocks="activitiesCostsSectionDescription" />
    </div>
    <template v-if="state.systemPriceGroups">
      <fixed-cost-section
        :mandatory-supplier-costs="mandatorySupplierCosts"
        :service-offer="serviceOffer"
        :page-cms="pageCms"
        :frequency-options="frequencyOptions"
        :read-only="readOnly"
        :service-activities-cms="serviceActivitiesCms"
        :supplier-info="supplierInfo"
        :system-price-groups="state.systemPriceGroups"
        @remove-mandatory-cost="(data) => emit('remove-mandatory-cost', data)"
        @new-mandatory-cost="(data) => emit('new-mandatory-cost', data)"
      />
      <variable-cost-section
        :independent-supplier-costs="independentSupplierCosts"
        :service-offer="serviceOffer"
        :page-cms="pageCms"
        :frequency-options="frequencyOptions"
        :read-only="readOnly"
        :service-activities-cms="serviceActivitiesCms"
        :supplier-info="supplierInfo"
        :system-price-groups="state.systemPriceGroups"
        @remove-independent-cost="
          (data) => emit('remove-independent-cost', data)
        "
        @new-independent-cost="(data) => emit('new-independent-cost', data)"
      />
    </template>
    <div v-else class="activities-costs-section__loader">
      <b-spinner type="grow" />
    </div>
  </div>
</template>

<script setup>
import FixedCostSection from "./FixedCostSection.vue";
import VariableCostSection from "./VariableCostSection.vue";
import { useSystemApi } from "@/services/api/useSystemApi";
import { computed, reactive } from "vue";
import CustomBlockContent from "@/components/CustomBlockContent/CustomBlockContent.vue";
import { useOfferTerminology } from "@/composables/useOfferTerminology";

const emit = defineEmits([
  "remove-mandatory-cost",
  "new-mandatory-cost",
  "remove-independent-cost",
  "new-independent-cost",
]);
const props = defineProps({
  serviceOffer: {
    type: Object,
    required: true,
  },
  mandatorySupplierCosts: {
    type: Array,
    required: true,
  },
  independentSupplierCosts: {
    type: Array,
    required: true,
  },
  pageCms: {
    type: Object,
    required: true,
  },
  frequencyOptions: {
    type: Object,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  serviceActivitiesCms: {
    type: Array,
    required: true,
  },
  supplierInfo: {
    type: Object,
    required: true,
  },
});

const systemApis = useSystemApi();
const { isEstimateMode } = useOfferTerminology();

const state = reactive({
  systemPriceGroups: undefined,
});

const activitiesCostsSectionCms = computed(() => {
  return props.pageCms.pageSections.find(
    (section) => section.label === "activitiesAndCost",
  );
});

const activitiesCostsSectionTitle = computed(() => {
  return "Kostnadsbeskrivelse";
});

const activitiesCostsSectionDescription = computed(() => {
  if (!activitiesCostsSectionCms.value?.content?.length) {
    return { content: "" };
  }

  const originalContent = activitiesCostsSectionCms.value.content[0];
  const modifiedContent = JSON.parse(JSON.stringify(originalContent));

  // Set appropriate text based on mode
  const descriptionText = isEstimateMode.value
    ? "Ved estimat er det viktig at totalbeløpet fremgår så nøyaktig som mulig. En tydelig og godt begrunnet kostnadsbeskrivelse gir kunden nødvendig trygghet og forutsigbarhet."
    : "Ved tilbud skal kostnadene beskrives nøyaktig. Oppgi et totalbeløp som gir kunden et klart bilde av hva leveransen vil koste. En presis kostnadsbeskrivelse bidrar til trygghet og tydelig forventningsavklaring.";

  // Apply the text to all text nodes
  if (modifiedContent.children) {
    modifiedContent.children.forEach((child) => {
      if (child.text) {
        child.text = descriptionText;
      }
    });
  }

  return modifiedContent;
});

const initialize = async () => {
  const response = await systemApis.getSystemPriceGroups(props.supplierInfo.id);
  state.systemPriceGroups = response.data;
};
initialize();
</script>

<style lang="scss" scoped>
.activities-costs-section {
  margin-top: 40px;

  &__title {
    font-weight: 600;
    font-size: $font-size-2xl;
    line-height: 29px;
    color: #1d1d1d;
    margin-bottom: 8px;
  }

  &__description {
    font-size: 16px;
    font-weight: 400;
  }

  &__loader {
    margin-top: 1.5rem;
  }
}
</style>
